import React from 'react';
import { Box, Button, Flex, IconButton, Text, Tooltip } from 'pounceV2';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import useModalV3 from 'Hooks/useModalV3';

interface ConversationHeaderActionsV3Props {
  isPrivate: boolean;
  isLoading: boolean;

  canEdit?: boolean;
  onPrint: () => void;
  onCopyLink: () => void;
  onTogglePrivacy: () => void;
  onDelete: () => void;
  /** Controls tooltip/aria text — "response" (AI page) or "triage" (alert sidesheet) */
  entityLabel?: string;
  /** Additional disabled check for the print button (e.g. no title available) */
  printDisabled?: boolean;
}

const ConversationHeaderActionsV3: React.FC<ConversationHeaderActionsV3Props> = ({
  isPrivate,
  isLoading,
  canEdit = true,
  onPrint,
  onCopyLink,
  onTogglePrivacy,
  onDelete,
  entityLabel = 'response',
  printDisabled = false,
}) => {
  const enablePrivacyControls = useFeatureToggle('ENABLE_AI_PRIVACY_CONTROLS');
  const { showModal, hideModal } = useModalV3();

  const handleConfirmDelete = React.useCallback(() => {
    hideModal();
    onDelete();
  }, [hideModal, onDelete]);

  const handleDeleteClick = React.useCallback(() => {
    showModal(
      <Box maxWidth={400}>
        <Text mb={6}>
          Are you sure you want to permanently delete this {entityLabel}? This action cannot be
          undone.
        </Text>
        <Flex justify="flex-end" gap={2}>
          <Button variant="outline" onClick={hideModal}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={isLoading} onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Flex>
      </Box>,
      { title: 'Confirm Delete', showCloseButton: true },
    );
  }, [showModal, hideModal, handleConfirmDelete, isLoading, entityLabel]);

  return (
    <>
      <Tooltip content={`Print ${entityLabel}`} placement="top">
        <Box as="span">
          <IconButton
            icon="print"
            aria-label={`Print ${entityLabel}`}
            variant="ghost"
            size="medium"
            disabled={isLoading || printDisabled}
            onClick={onPrint}
          />
        </Box>
      </Tooltip>
      <Tooltip content="Copy URL" placement="top">
        <Box as="span">
          <IconButton
            icon="copy-link"
            aria-label="Copy URL"
            variant="ghost"
            size="medium"
            disabled={isLoading}
            onClick={onCopyLink}
          />
        </Box>
      </Tooltip>
      {enablePrivacyControls && (
        <Tooltip
          content={
            isPrivate
              ? 'This chat is private. Click to make it shared and visible to your organization'
              : 'This chat is shared. Click to make it private and only visible to you'
          }
          placement="top"
        >
          <Box as="span">
            <IconButton
              icon={isPrivate ? 'lock' : 'shared'}
              aria-label={isPrivate ? 'Make chat shared' : 'Make chat private'}
              variant="ghost"
              size="medium"
              borderStyle="circle"
              disabled={isLoading || !canEdit}
              onClick={onTogglePrivacy}
            />
          </Box>
        </Tooltip>
      )}
      <Tooltip content={`Permanently delete this ${entityLabel}`} placement="top">
        <Box as="span">
          <IconButton
            icon="trash"
            aria-label={`Delete ${entityLabel}`}
            variant="ghost"
            size="medium"
            disabled={isLoading || !canEdit}
            onClick={handleDeleteClick}
          />
        </Box>
      </Tooltip>
    </>
  );
};

export default React.memo(ConversationHeaderActionsV3);
